// Keyboard model of a tablist (JDEV-3207; also for the editor tabs, JDEV-3208). WAI-ARIA tabs pattern with
// manual activation, because a tab switch loads a whole component (column header) or a big form (editor):
// - Roving tabindex: one tab is the Tab stop (tabindex 0), the others are -1. That is the focused tab while
//   focus is in the list, and the selected tab again once focus leaves it.
// - Left/Right move to the previous/next tab and wrap; mirrored when the list is right-to-left. Home/End go to
//   the first/last tab. Only `ids` take part: pass the tabs that are shown (priority+ leaves the rest in the menu).
// - Enter/Space activate the focused tab (onActivate). Mouse activation is the tab's own click handler.
// - Grid (`columns` > 0, the column tabs' button grid of JDEV-3370): Up/Down move to the tab above/below in the
//   same grid column. Down from a column the last row doesn't reach goes to the last tab; nothing wraps.
//
// Bind on the tablist element: @keydown="onKeydown" @focusin="onFocusin" @focusout="onFocusout", and on each
// tab: :tabindex="tabindexFor(id)" data-tab-id="<id>". The tab element is [role="tab"] inside that element (or
// the element itself).

import { ref, watch, type Ref } from 'vue'

export interface RovingTabsOptions {
  /** The tablist element. */
  list: Ref<HTMLElement | null>
  /** The ids that can take focus, in visual order. */
  ids: Ref<string[]>
  selectedId: Ref<string | null | undefined>
  onActivate: (id: string) => void
  /** The tabs are laid out in a grid of this many columns (row-major): Up/Down move by rows. 0 or unset: one row. */
  columns?: Ref<number>
}

export function useRovingTabs ({ list, ids, selectedId, onActivate, columns }: RovingTabsOptions) {
  // The tab that holds the Tab stop while focus is inside the list; null = the selected tab.
  const focusedId = ref<string | null>(null)

  const stopId = () => {
    const all = ids.value
    if (focusedId.value && all.includes(focusedId.value)) return focusedId.value
    if (selectedId.value && all.includes(selectedId.value)) return selectedId.value
    return all[0] ?? null
  }
  const tabindexFor = (id: string) => (id === stopId() ? 0 : -1)

  function tabEl (id: string): HTMLElement | null {
    const holder = list.value?.querySelector<HTMLElement>(`[data-tab-id="${CSS.escape(id)}"]`)
    if (!holder) return null
    return holder.matches('[role="tab"]') ? holder : holder.querySelector<HTMLElement>('[role="tab"]')
  }

  function focusTab (id: string) {
    focusedId.value = id
    tabEl(id)?.focus({ preventScroll: true })
  }

  function idOf (target: EventTarget | null) {
    const tab = (target as HTMLElement | null)?.closest?.('[role="tab"]')
    if (!tab || !list.value?.contains(tab)) return null
    return tab.closest<HTMLElement>('[data-tab-id]')?.dataset.tabId ?? null
  }

  function onKeydown (e: KeyboardEvent) {
    const current = idOf(e.target)
    if (current == null || e.altKey || e.ctrlKey || e.metaKey) return
    const all = ids.value
    const pos = all.indexOf(current)
    const rtl = !!list.value && getComputedStyle(list.value).direction === 'rtl'
    const next = rtl ? 'ArrowLeft' : 'ArrowRight'
    const prev = rtl ? 'ArrowRight' : 'ArrowLeft'
    let to: string | undefined
    if (e.key === next) to = all[(pos + 1) % all.length]
    else if (e.key === prev) to = all[(pos - 1 + all.length) % all.length]
    else if (e.key === 'Home') to = all[0]
    else if (e.key === 'End') to = all[all.length - 1]
    else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && columns && columns.value > 0) {
      const cols = columns.value
      if (e.key === 'ArrowUp') to = pos - cols >= 0 ? all[pos - cols] : undefined
      else if (pos + cols < all.length) to = all[pos + cols]
      else if (Math.floor(pos / cols) < Math.floor((all.length - 1) / cols)) to = all[all.length - 1]
    }
    else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      // Handled here (not by the button's synthetic click) so links and divs work too, and only once.
      e.preventDefault()
      onActivate(current)
      return
    } else return
    e.preventDefault()
    if (to != null) focusTab(to)
  }

  function onFocusin (e: FocusEvent) {
    const id = idOf(e.target)
    if (id != null) focusedId.value = id
  }

  function onFocusout (e: FocusEvent) {
    const to = e.relatedTarget as Node | null
    if (!to || !list.value?.contains(to)) focusedId.value = null
  }

  // A focused tab that was moved to the menu gives the Tab stop back to the selected tab.
  watch(ids, (all) => {
    if (focusedId.value && !all.includes(focusedId.value)) focusedId.value = null
  })

  return { focusedId, tabindexFor, onKeydown, onFocusin, onFocusout, focusTab }
}
